import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Grid, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../utils/axios';
import OrganizationDialog from './OrganizationDialog';
import ProgramDialog from './ProgramDialog';

const AssayForm = ({ inputConfig, onChange }) => {
    const [config, setConfig] = useState({
        approved_users: [],
        assay_name: '',
        config_directory: '',
        status: 'NEW',
        config_type: '',
        cro_contact: '',
        data_file_cro: '',
        data_file_program: '',
        labels: '',
        plugin: 'acas_assay',
        preprocessor: 'standard_preprocessor',
        validator: 'standard_validator',
        loader: 'standard_loader',
        point_of_contact_id: '',
        provider_organization_id: '',
        issue_program_id: '',
    });
    const [assayProviders, setAssayProviders] = useState([]);
    const [pointOfContacts, setPointOfContacts] = useState([]);
    const [showAddCROModal, setShowAddCROModal] = useState(false);
    const [showAddProgramModal, setShowAddProgramModal] = useState(false);
    const [programs, setPrograms] = useState([]);
    const [plugins, setPlugins] = useState([{ name: 'acas_assay' }]);

    useEffect(() => {
        fetchAssayProviders();
        fetchPrograms();
        fetchPointOfContacts();
        fetchPlugins();

        if (inputConfig != null) {
            // Sync config with inputConfig from the parent
            setConfig(inputConfig);
        }

        console.dir("Assay Form use effect")
        console.dir(inputConfig)

    }, [inputConfig]);

    // Fetch AssayProviders
    const fetchAssayProviders = async () => {
        try {
            const response = await axiosInstance.get('/organizations/?organization_type=Assay+Provider');
            setAssayProviders(response.data);
        } catch (error) {
            console.error('Error fetching AssayProviders:', error);
        }
    };

    // Fetch Programs
    const fetchPrograms = async () => {
        try {
            const response = await axiosInstance.get('/programs/');
            setPrograms(response.data);
        } catch (error) {
            console.error('Error fetching Programs:', error);
        }
    };

    // Fetch PointOfContacts
    const fetchPointOfContacts = async () => {
        try {
            const subdomain = process.env.REACT_APP_SHADOWSTREAM_SUB || 'genflux';
            const response = await axiosInstance.get('/users/?profile__organization__name=' + subdomain);
            setPointOfContacts(response.data);
        } catch (error) {
            console.error('Error fetching PointOfContacts:', error);
        }
    };

    const fetchPlugins = async () => {
        try {
            const response = await axiosInstance.get('v1/plugins');
            if (Array.isArray(response.data) && response.data.length > 0) {
                setPlugins(response.data);
            } else {
                setPlugins([{ name: 'acas_assay' }]);
            }
        } catch (error) {
            console.error('Error fetching Plugins:', error);
            setPlugins([{ name: 'acas_assay' }]);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedConfig = { ...config, [name]: value };

        setConfig(updatedConfig);  // Update local config state
        onChange(updatedConfig);   // Pass the updated config to parent component
    };

    const openAddCROModal = () => {
        setShowAddCROModal(true);
    };

    const cancelledAddCROModal = () => {
        setShowAddCROModal(false);
    };

    const completedAddCROModal = () => {
        setShowAddCROModal(false);
    };

    const openAddProgramModal = () => {
        setShowAddProgramModal(true);
    };

    const cancelledAddProgramModal = () => {
        setShowAddProgramModal(false);
    };

    const completedAddProgramModal = (newProgram) => {
        console.dir('newProgram')
        console.dir(newProgram)
        const updatedConfig = { ...config, ['issue_program_id']: newProgram.id };
        setConfig(updatedConfig);  // Update local config state

        setShowAddProgramModal(false);
        fetchPrograms();
    };

    return (
        <div style={{ padding: '20px', margin: '20px', minHeight: '300px' }}>
            <h3>Define Assay Properties - Step 1</h3>

            <Grid container spacing={2} padding={1}>
                <Grid item xs={5}>
                    <FormControl variant="outlined" className="styled-form-control" fullWidth>
                        <InputLabel id="assayProvider-label" shrink>
                            Assay Provider
                        </InputLabel>
                        <Select
                            labelId="assayProvider-label"
                            id="assayProvider-select"
                            name="provider_organization_id"
                            value={config.provider_organization_id || ''}
                            onChange={handleInputChange}
                            label="Assay Provider"
                        >
                            <MenuItem value="" disabled>
                                Please select an Assay Provider
                            </MenuItem>
                            {assayProviders.map((provider) => (
                                <MenuItem key={provider.id} value={provider.id}>
                                    {provider.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1}>
                    <IconButton
                        style={{
                            backgroundColor: '#e0e0e0',
                            borderRadius: '4px',
                            padding: '16px',
                        }}
                        onClick={openAddCROModal}
                    >
                        <AddIcon style={{ color: '#6200ea' }} />
                    </IconButton>
                </Grid>

                <Grid item xs={6}>
                    <FormControl className="styled-form-control" fullWidth>
                        <TextField
                            id="assay-select"
                            name="data_grouping_name"
                            label="Assay Name"
                            variant="outlined"
                            fullWidth
                            value={config.data_grouping_name || ''}
                            onChange={handleInputChange}
                        />
                    </FormControl>
                </Grid>

                <Grid item xs={5}>
                    <FormControl variant="outlined" className="styled-form-control" fullWidth>
                        <InputLabel id="program-label">
                            Program Name
                        </InputLabel>
                        <Select
                            labelId="program-label"
                            id="program-select"
                            name="issue_program_id"
                            value={config.issue_program_id || ''}
                            onChange={handleInputChange}
                            label="Program Name"
                        >
                            <MenuItem value="" disabled>
                                Please select a Program
                            </MenuItem>
                            {programs.map((program) => (
                                <MenuItem key={program.id} value={program.id}>
                                    {program.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1}>
                    <IconButton
                        style={{
                            backgroundColor: '#e0e0e0',
                            borderRadius: '4px',
                            padding: '16px',
                        }}
                        onClick={openAddProgramModal}
                    >
                        <AddIcon style={{ color: '#6200ea' }} />
                    </IconButton>
                </Grid>

                <Grid item xs={6}>
                    <FormControl variant="outlined" className="styled-form-control" fullWidth>
                        <InputLabel id="plugin-label">Stream Plugin</InputLabel>
                        <Select
                            labelId="plugin-label"
                            id="plugin-select"
                            name="plugin"
                            value={config.plugin}
                            onChange={handleInputChange}
                            label="Plugin"
                        >
                            <MenuItem value="" disabled>
                                Please select a Plugin
                            </MenuItem>
                            {plugins.map((plugin) => (
                                <MenuItem key={plugin.name} value={plugin.name}>
                                    {plugin.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={5}>
                    <FormControl variant="outlined" className="styled-form-control" fullWidth>
                        <InputLabel id="pointOfContact-label">
                            Point of Contact
                        </InputLabel>
                        <Select
                            labelId="pointOfContact-label"
                            id="pointOfContact-select"
                            name="point_of_contact_id"  // Correct field name
                            value={config.point_of_contact_id || ''}  // Make sure this value reflects the correct state
                            onChange={handleInputChange}
                            label="Point of Contact"
                        >
                            <MenuItem value="" disabled>
                                Please select a Point of Contact
                            </MenuItem>
                            {pointOfContacts.map((contact) => (
                                <MenuItem key={contact.id} value={contact.id}>
                                    {contact.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* CRO modal for adding Assay Providers */}
            <OrganizationDialog
                inputEntity={null}
                open={showAddCROModal}
                onClose={cancelledAddCROModal}
                onSubmit={completedAddCROModal}
                fixedType={"Assay Provider"}
            />
            <ProgramDialog
                open={showAddProgramModal}
                onClose={cancelledAddProgramModal}
                programData={null}
                onSubmit={completedAddProgramModal}
                simpleMode={true}
                    />


        </div>
    );
};

export default AssayForm;
